import api from '../api';

export interface Vendor {
  id: number;
  tenant_id: number;
  vendor_name: string;
  vendor_slug: string;
  tier: 1 | 2;
  service_description: string;
  service_category: string;
  data_access_level: 'full_access' | 'transit_only' | 'metadata_only' | 'no_access';
  data_types_accessed: string[];
  soc2_status: 'current' | 'expired' | 'pending' | 'not_applicable';
  soc2_report_date: string | null;
  soc2_expiration_date: string | null;
  soc2_report_url: string | null;
  iso27001_certified: boolean;
  iso27001_expiration_date: string | null;
  dpa_status: 'signed' | 'pending' | 'not_required' | 'declined';
  dpa_signed_date: string | null;
  dpa_expiration_date: string | null;
  security_certifications: string[];
  data_residency: string | null;
  data_residency_regions: string[];
  encryption_at_rest: string | null;
  encryption_in_transit: string | null;
  zero_data_retention: boolean;
  data_retention_policy: string | null;
  data_deletion_process: string | null;
  ai_training_prohibited: boolean;
  subprocessors_disclosed: boolean;
  subcontractor_type: 'hosting' | 'infrastructure' | 'ai_processing' | 'storage' | 'database' | 'other' | 'not_subcontractor';
  breach_notification_hours: number;
  right_to_audit: boolean;
  contract_start_date: string | null;
  contract_end_date: string | null;
  contract_value_annual: number | null;
  contract_auto_renews: boolean;
  payment_terms: string | null;
  primary_contact_name: string | null;
  primary_contact_email: string | null;
  primary_contact_phone: string | null;
  support_url: string | null;
  security_page_url: string | null;
  trust_center_url: string | null;
  status_page_url: string | null;
  last_review_date: string | null;
  next_review_date: string | null;
  review_frequency_months: number;
  review_status: 'current' | 'pending' | 'overdue' | 'in_progress';
  risk_score: number;
  risk_score_factors: Record<string, { value: number; reason: string }>;
  risk_level: 'critical' | 'high' | 'medium' | 'low';
  business_owner_user_id: number | null;
  technical_owner_user_id: number | null;
  integration_type: string | null;
  integration_details: Record<string, unknown>;
  notes: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  onboarding_checklist: Record<string, boolean>;
  onboarding_completed: boolean;
  onboarding_completed_date: string | null;
  is_active: boolean;
  archived_at: string | null;
  archive_reason: string | null;
  created_at: string;
  updated_at: string;
  documents?: VendorDocument[];
  reviews?: VendorReview[];
  alerts?: VendorAlert[];
  contacts?: VendorContact[];
}

export interface VendorDocument {
  id: number;
  vendor_id: number;
  document_type: string;
  document_name: string;
  document_description: string | null;
  file_path: string | null;
  file_url: string | null;
  file_size: number | null;
  external_url: string | null;
  is_external_link: boolean;
  document_date: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  version: string | null;
  is_current_version: boolean;
  review_status: 'pending' | 'reviewed' | 'approved' | 'rejected' | 'expired';
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  confidentiality_level: 'public' | 'internal' | 'confidential' | 'restricted';
  uploaded_by: number | null;
  created_at: string;
  updated_at: string;
  uploader?: { id: number; name: string; email: string };
}

export interface VendorReview {
  id: number;
  vendor_id: number;
  review_type: 'annual' | 'quarterly' | 'incident_triggered' | 'contract_renewal' | 'onboarding' | 'offboarding' | 'ad_hoc';
  review_period_start: string | null;
  review_period_end: string | null;
  review_status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  soc2_reviewed: boolean;
  soc2_findings: string | null;
  soc2_exceptions: unknown[];
  dpa_reviewed: boolean;
  dpa_findings: string | null;
  security_posture_reviewed: boolean;
  security_findings: string | null;
  security_concerns: unknown[];
  service_performance_reviewed: boolean;
  performance_findings: string | null;
  uptime_percentage: number | null;
  incident_count: number;
  contract_reviewed: boolean;
  contract_findings: string | null;
  pricing_changes: string | null;
  continued_use_recommended: boolean;
  recommendation: 'continue' | 'continue_with_conditions' | 'evaluate_alternatives' | 'initiate_offboarding' | 'immediate_action_required' | null;
  recommendation_rationale: string | null;
  action_items: unknown[];
  action_items_completed: number;
  action_items_total: number;
  risk_score_before: number | null;
  risk_score_after: number | null;
  risk_score_change: number | null;
  overall_assessment: string | null;
  executive_summary: string | null;
  reviewed_by: number | null;
  review_started_at: string | null;
  review_completed_at: string | null;
  approved_by: number | null;
  approved_at: string | null;
  approval_notes: string | null;
  next_review_date: string | null;
  created_at: string;
  updated_at: string;
  vendor?: { id: number; vendor_name: string; tier: number };
  reviewer?: { id: number; name: string; email: string };
}

export interface VendorAlert {
  id: number;
  vendor_id: number;
  alert_type: string;
  alert_severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  alert_title: string;
  alert_message: string;
  alert_details: Record<string, unknown>;
  due_date: string | null;
  days_until_due: number | null;
  status: 'active' | 'acknowledged' | 'resolved' | 'snoozed' | 'dismissed';
  acknowledged_by: number | null;
  acknowledged_at: string | null;
  resolved_by: number | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  snoozed_until: string | null;
  snooze_count: number;
  notification_sent: boolean;
  notification_sent_at: string | null;
  auto_generated: boolean;
  created_at: string;
  updated_at: string;
  vendor?: { id: number; vendor_name: string; tier: number };
}

export interface VendorContact {
  id: number;
  vendor_id: number;
  contact_type: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorDependency {
  id: number;
  package_name: string;
  package_version: string | null;
  package_type: 'npm' | 'pip' | 'gem' | 'composer' | 'maven' | 'nuget' | 'go' | 'other';
  registry_url: string | null;
  homepage_url: string | null;
  repository_url: string | null;
  license_type: string | null;
  license_spdx: string | null;
  is_copyleft: boolean;
  license_compatible: boolean;
  latest_version: string | null;
  is_outdated: boolean;
  versions_behind: number;
  is_deprecated: boolean;
  deprecation_message: string | null;
  is_eol: boolean;
  eol_date: string | null;
  vulnerability_count: number;
  critical_vulnerabilities: number;
  high_vulnerabilities: number;
  medium_vulnerabilities: number;
  low_vulnerabilities: number;
  vulnerabilities: unknown[];
  last_scan_date: string | null;
  scan_source: string | null;
  is_direct_dependency: boolean;
  parent_package: string | null;
  dependency_depth: number;
  risk_score: number;
  risk_factors: unknown[];
  created_at: string;
  updated_at: string;
}

export interface VendorStats {
  total: number;
  tier1Count: number;
  tier2Count: number;
  soc2Current: number;
  soc2Expired: number;
  dpaSigned: number;
  dpaPending: number;
  reviewsCurrent: number;
  reviewsOverdue: number;
  riskCritical: number;
  riskHigh: number;
  riskMedium: number;
  riskLow: number;
  activeAlerts: number;
  criticalAlerts: number;
  highAlerts: number;
}

export interface DependencyStats {
  total: number;
  npm: number;
  outdated: number;
  deprecated: number;
  copyleft: number;
  withVulnerabilities: number;
  criticalVulnerabilities: number;
  highVulnerabilities: number;
  mediumVulnerabilities: number;
  lowVulnerabilities: number;
  directDependencies: number;
  highRisk: number;
  mediumRisk: number;
  lowRisk: number;
  lastScanDate: string | null;
  licenseBreakdown: Record<string, number>;
}

export interface AlertSummary {
  total: number;
  bySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
  };
  byType: Record<string, number>;
  byStatus: {
    active: number;
    acknowledged: number;
  };
  overdue: number;
  dueSoon: number;
}

export interface VendorFilters {
  tier?: number;
  is_active?: boolean;
  service_category?: string;
  soc2_status?: string;
  dpa_status?: string;
  review_status?: string;
  risk_level?: string;
  subcontractor_type?: string;
  subcontractors_only?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: 'ASC' | 'DESC';
}

export interface CreateVendorData {
  vendor_name: string;
  tier: 1 | 2;
  service_description: string;
  service_category: string;
  data_access_level: string;
  data_types_accessed?: string[];
  soc2_status?: string;
  soc2_expiration_date?: string;
  iso27001_certified?: boolean;
  dpa_status?: string;
  dpa_signed_date?: string;
  security_certifications?: string[];
  data_residency?: string;
  encryption_at_rest?: string;
  encryption_in_transit?: string;
  zero_data_retention?: boolean;
  ai_training_prohibited?: boolean;
  breach_notification_hours?: number;
  right_to_audit?: boolean;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_value_annual?: number;
  support_url?: string;
  security_page_url?: string;
  trust_center_url?: string;
  status_page_url?: string;
  notes?: string;
  tags?: string[];
  subcontractor_type?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

class VendorAPI {
  async getStats(): Promise<VendorStats> {
    const response = await api.get<ApiResponse<VendorStats>>('/api/vendors/stats');
    return response.data.data;
  }

  async getVendors(filters?: VendorFilters): Promise<Vendor[]> {
    const response = await api.get<ApiResponse<Vendor[]>>('/api/vendors', { params: filters });
    return response.data.data;
  }

  async getVendorById(id: number): Promise<Vendor> {
    const response = await api.get<ApiResponse<Vendor>>(`/api/vendors/${id}`);
    return response.data.data;
  }

  async createVendor(data: CreateVendorData): Promise<Vendor> {
    const response = await api.post<ApiResponse<Vendor>>('/api/vendors', data);
    return response.data.data;
  }

  async updateVendor(id: number, data: Partial<CreateVendorData>): Promise<Vendor> {
    const response = await api.put<ApiResponse<Vendor>>(`/api/vendors/${id}`, data);
    return response.data.data;
  }

  async archiveVendor(id: number, reason: string): Promise<Vendor> {
    const response = await api.post<ApiResponse<Vendor>>(`/api/vendors/${id}/archive`, { reason });
    return response.data.data;
  }

  async getDocuments(vendorId: number, filters?: { document_type?: string; is_current_version?: boolean }): Promise<VendorDocument[]> {
    const response = await api.get<ApiResponse<VendorDocument[]>>(`/api/vendors/${vendorId}/documents`, { params: filters });
    return response.data.data;
  }

  async uploadDocument(vendorId: number, formData: FormData): Promise<VendorDocument> {
    const response = await api.post<ApiResponse<VendorDocument>>(`/api/vendors/${vendorId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  }

  async updateDocumentStatus(docId: number, review_status: 'pending' | 'approved' | 'expired'): Promise<VendorDocument> {
    const response = await api.patch<ApiResponse<VendorDocument>>(`/api/vendors/documents/${docId}`, { review_status });
    return response.data.data;
  }

  async getReviews(vendorId: number, filters?: { review_status?: string; review_type?: string }): Promise<VendorReview[]> {
    const response = await api.get<ApiResponse<VendorReview[]>>(`/api/vendors/${vendorId}/reviews`, { params: filters });
    return response.data.data;
  }

  async getAllReviews(filters?: { review_status?: string; review_type?: string }): Promise<VendorReview[]> {
    const response = await api.get<ApiResponse<VendorReview[]>>('/api/vendors/reviews/all', { params: filters });
    return response.data.data;
  }

  async createReview(vendorId: number, data: { review_type: string; review_period_start?: string; review_period_end?: string }): Promise<VendorReview> {
    const response = await api.post<ApiResponse<VendorReview>>(`/api/vendors/${vendorId}/reviews`, data);
    return response.data.data;
  }

  async completeReview(reviewId: number, data: Partial<VendorReview>): Promise<VendorReview> {
    const response = await api.put<ApiResponse<VendorReview>>(`/api/vendors/reviews/${reviewId}/complete`, data);
    return response.data.data;
  }

  async getContacts(vendorId: number): Promise<VendorContact[]> {
    const response = await api.get<ApiResponse<VendorContact[]>>(`/api/vendors/${vendorId}/contacts`);
    return response.data.data;
  }

  async addContact(vendorId: number, data: Partial<VendorContact>): Promise<VendorContact> {
    const response = await api.post<ApiResponse<VendorContact>>(`/api/vendors/${vendorId}/contacts`, data);
    return response.data.data;
  }

  async updateContact(contactId: number, data: Partial<VendorContact>): Promise<VendorContact> {
    const response = await api.put<ApiResponse<VendorContact>>(`/api/vendors/contacts/${contactId}`, data);
    return response.data.data;
  }

  async deleteContact(contactId: number): Promise<void> {
    await api.delete(`/api/vendors/contacts/${contactId}`);
  }

  async updateOnboardingChecklist(vendorId: number, checklist: Record<string, boolean>): Promise<Vendor> {
    const response = await api.put<ApiResponse<Vendor>>(`/api/vendors/${vendorId}/onboarding-checklist`, checklist);
    return response.data.data;
  }

  async getOnboardingChecklistTemplate(): Promise<Record<string, boolean>> {
    const response = await api.get<ApiResponse<Record<string, boolean>>>('/api/vendors/onboarding/checklist-template');
    return response.data.data;
  }

  async getAlerts(filters?: { status?: string; alert_severity?: string; alert_type?: string }): Promise<VendorAlert[]> {
    const response = await api.get<ApiResponse<VendorAlert[]>>('/api/vendors/alerts/all', { params: filters });
    return response.data.data;
  }

  async getAlertSummary(): Promise<AlertSummary> {
    const response = await api.get<ApiResponse<AlertSummary>>('/api/vendors/alerts/summary');
    return response.data.data;
  }

  async acknowledgeAlert(alertId: number): Promise<VendorAlert> {
    const response = await api.post<ApiResponse<VendorAlert>>(`/api/vendors/alerts/${alertId}/acknowledge`);
    return response.data.data;
  }

  async resolveAlert(alertId: number, resolution_notes: string): Promise<VendorAlert> {
    const response = await api.post<ApiResponse<VendorAlert>>(`/api/vendors/alerts/${alertId}/resolve`, { resolution_notes });
    return response.data.data;
  }

  async snoozeAlert(alertId: number, snooze_until: string): Promise<VendorAlert> {
    const response = await api.post<ApiResponse<VendorAlert>>(`/api/vendors/alerts/${alertId}/snooze`, { snooze_until });
    return response.data.data;
  }

  async generateAlerts(): Promise<{ generated: number; alerts: VendorAlert[] }> {
    const response = await api.post<ApiResponse<{ generated: number; alerts: VendorAlert[] }>>('/api/vendors/alerts/generate');
    return response.data.data;
  }

  async getDependencies(filters?: { package_type?: string; is_outdated?: boolean; is_copyleft?: boolean; has_vulnerabilities?: boolean; search?: string; sort_by?: string }): Promise<VendorDependency[]> {
    const response = await api.get<ApiResponse<VendorDependency[]>>('/api/vendors/dependencies/all', { params: filters });
    return response.data.data;
  }

  async getDependencyStats(): Promise<DependencyStats> {
    const response = await api.get<ApiResponse<DependencyStats>>('/api/vendors/dependencies/stats');
    return response.data.data;
  }

  async scanDependencies(packageJsonPath?: string): Promise<{ scanned: number; dependencies: VendorDependency[] }> {
    const response = await api.post<ApiResponse<{ scanned: number; dependencies: VendorDependency[] }>>('/api/vendors/dependencies/scan', { package_json_path: packageJsonPath });
    return response.data.data;
  }

  async deleteDependency(id: number): Promise<void> {
    await api.delete(`/api/vendors/dependencies/${id}`);
  }

  async clearDependencies(): Promise<void> {
    await api.delete('/api/vendors/dependencies/clear');
  }

  async getAuditLog(vendorId?: number, filters?: { action?: string; entity_type?: string; limit?: number; offset?: number }): Promise<unknown[]> {
    const url = vendorId ? `/api/vendors/${vendorId}/audit-log` : '/api/vendors/audit-log/all';
    const response = await api.get<ApiResponse<unknown[]>>(url, { params: filters });
    return response.data.data;
  }

  async exportPDF(tier?: number): Promise<Blob> {
    const response = await api.get('/api/vendors/export/pdf', {
      params: { tier },
      responseType: 'blob'
    });
    return response.data;
  }

  async exportExcel(tier?: number): Promise<Blob> {
    const response = await api.get('/api/vendors/export/excel', {
      params: { tier },
      responseType: 'blob'
    });
    return response.data;
  }

  async exportComplianceMatrix(): Promise<Blob> {
    const response = await api.get('/api/vendors/export/compliance-matrix', {
      responseType: 'blob'
    });
    return response.data;
  }

  async exportSubcontractorList(): Promise<Blob> {
    const response = await api.get('/api/vendors/export/subcontractor-list', {
      responseType: 'blob'
    });
    return response.data;
  }

  async getSubcontractors(): Promise<Vendor[]> {
    const response = await api.get<ApiResponse<Vendor[]>>('/api/vendors/subcontractors');
    return response.data.data;
  }

  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const vendorAPI = new VendorAPI();
export default vendorAPI;
